<template>
  <ClickWrapper
    v-if="visible"
    ref="menu"
    class="base-context-menu"
    role="menu"
    aria-label="Kontextmenü"
    aria-description="Liste verfügbarer Aktionen"
    :include="target"
    :style="{ ...pos, ...menuStyles }"
    @outside="closeMenu"
    @keydown.native="handleKeydown"
  >
    <ul
      v-if="hasActions"
    >
      <BaseContextMenuItem
        v-for="(action, inx) in actions"
        ref="contextMenuItems"
        :key="`${inx}-${action.method}`"
        v-bind="action"
        :method="action.method"
        :target="target"
        @click.native="dismissMenu"
        @keydown.enter.native="dismissMenu"
        @keydown.space.native="dismissMenu"
      />
    </ul>
    <ul
      v-if="hasContent"
      v-html="content"
    />
  </ClickWrapper>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { storeToRefs } from 'pinia';
import { position, dimensions } from '@/helper/HtmlCss';

import BaseContextMenuItem from './BaseContextMenuItem';
import ClickWrapper from '../ClickWrapper';

import { useAccessibilityStore } from '@/apps/base/stores';

export default {
  name: 'BaseContextMenu',

  components: {
    BaseContextMenuItem,
    ClickWrapper,
  },

  emits: ['clear', 'action'],

  setup() {
    const accessibilityStore = useAccessibilityStore();
    const { closeAccessibilityMenu, handleAccessibilityMenu } = accessibilityStore;
    const { accessibilityMenu } = storeToRefs(accessibilityStore);

    return {
      accessibilityMenu,
      closeAccessibilityMenu,
      handleAccessibilityMenu,
    };
  },

  data() {
    return {
      contextMenuItems: [],
      pos: {
        top: null,
        right: null,
        bottom: null,
        left: null,
      },
      menuStyles: {
        height: null,
        'max-height': null,
      },
      initialized: false,
    };
  },

  computed: {
    ...mapState('contextMenu', [
      'target',
      'actions',
      'content',
    ]),
    ...mapGetters('contextMenu', [
      'visible',
      'hasActions',
      'hasContent',
    ]),
  },

  watch: {
    target(newTarget) {
      if (newTarget) {
        this.$nextTick(() => {
          this.placeMenu(newTarget);
        });
      }
    },
    visible(isVisible) {
      if (isVisible) {
        this.$nextTick(() => {
          this.placeMenu(this.target);
        });
      }
    },
  },

  methods: {
    ...mapActions('contextMenu', [
      'HIDE',
    ]),
    closeMenu() {
      this.HIDE();
      this.closeAccessibilityMenu();
      this.initialized = false;
      window.removeEventListener('scroll', this.closeMenu);
    },
    dismissMenu() {
      this.HIDE();
      this.initialized = false;
      window.removeEventListener('scroll', this.closeMenu);
    },
    handleKeydown(e) {
      this.handleAccessibilityMenu(e, this.closeMenu, { trapFocus: true });
    },
    placeMenu(target) {
      if (this.$refs.menu && target) {
        this.menuStyles = {};
        this.pos = {};
        const element = target;
        const menu = ('$el' in this.$refs.menu) ? this.$refs.menu.$el : this.$refs.menu;

        this.$nextTick(() => {
          const header = document.querySelector('header > .navbar');
          const elementPosition = position(element)[0];
          const menuDimensions = dimensions(menu)[0];

          const viewportOffset = {
            top: header ? header.scrollHeight : 0,
            left: 0,
            bottom: 0,
            right: 0,
          };
          const viewport = {
            width: window.innerWidth,
            height: window.innerHeight,
          };
          const validViewport = {
            top: viewportOffset.top,
            left: viewportOffset.left,
            bottom: viewportOffset.bottom,
            right: viewportOffset.right,
            width: viewport.width - viewportOffset.left - viewportOffset.right,
            height: viewport.height - viewportOffset.top - viewportOffset.bottom,
          };

          const newMenuPosition = {
            top: elementPosition.y,
            left: elementPosition.x - menuDimensions.width,
            width: 'auto',
          };

          const collision = {
            top: newMenuPosition.top < validViewport.top,
            left: newMenuPosition.left < validViewport.left,
            bottom: newMenuPosition.top + menuDimensions.height
              > validViewport.height + validViewport.top,
            right: newMenuPosition.left + menuDimensions.width
              > validViewport.width + validViewport.left,
            vertical: menuDimensions.height > validViewport.height,
            horizontal: menuDimensions.width > validViewport.width,
          };

          if (collision.vertical) {
            newMenuPosition.top = validViewport.top;
            newMenuPosition.height = validViewport.height;
          }
          if (collision.horizontal) {
            newMenuPosition.left = validViewport.left;
            newMenuPosition.width = validViewport.width;
          }
          if (collision.top) {
            newMenuPosition.top = validViewport.top;
          }
          if (collision.left) {
            newMenuPosition.left = validViewport.left;
          }
          if (collision.bottom) {
            newMenuPosition.top = validViewport.height + validViewport.top - menuDimensions.height;
          }
          if (collision.right) {
            newMenuPosition.left = validViewport.width + validViewport.left - menuDimensions.width;
          }

          Object.entries(newMenuPosition).forEach(([key, value]) => {
            if (key === 'width') menu.style[key] = value;
            else menu.style[key] = `${value}px`;
          });

          if (this.initialized) return;

          if (this.actions.length) {
            this.contextMenuItems = this.$refs.contextMenuItems;
            this.contextMenuItems.forEach((ref) => {
              const el = ('$el' in ref) ? ref.$el : ref;
              this.accessibilityMenu.elements.push(el.querySelector('a, span'));
            });
          } else {
            const elements = menu.querySelectorAll('a');
            elements.forEach((el) => {
              const labelText = el.textContent?.trim() || '';
              el.setAttribute('aria-label', labelText);
              el.setAttribute('role', 'menuitem');
              el.classList.add('accessibility-outline');
              el.classList.add('outline-inset');
            });
            this.accessibilityMenu.elements = Array.from(elements);
          }

          const firstItem = this.accessibilityMenu.elements[0];
          if (firstItem) {
            firstItem.focus();
          }

          this.initialized = true;
        });
      }
    },
  },
};
</script>

<style lang="scss">
.base-context-menu {
    position: fixed;
    width: auto;
    z-index: 5100; /* Above modals (5000), below tooltips (5500) */
    background-color: #fff;
    box-shadow: 0 10px 20px rgba(112, 112, 112, .3);
    text-align: left;
    border-radius: 7px;
    overflow: auto;
    height: auto;

    ul {
        display: block;
        list-style: none;
        padding: 0;

        li {
            padding: 0 !important;

            a, span {
                padding: .5em 3.75em .5em 1em;
                display: block;
                color: #3D3D3D;
                font-size: 1rem;
                cursor: pointer;
                text-decoration: none;
                font-weight: 400;

                &:hover {
                    background-color: #F2F2F2;
                    text-decoration: none;
                }
            }

            + li {
                border-top: 1px solid #EAEAEA;
            }
        }
    }
}
</style>
